import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Row, Col, Card, Button, Badge, Alert } from 'react-bootstrap';
import { getBreed } from '../services/breedService';
import { getPups } from '../services/pupService';
import { Breed, Pup } from '../types/breed';
import PupCard from '../components/PupCard';

const BreedDetailPage: React.FC = () => {
  const { breedId } = useParams<{ breedId: string }>();

  const [breed, setBreed] = useState<Breed | null>(null);
  const [pups, setPups] = useState<Pup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBreedData = async () => {
      if (!breedId) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch breed details
        const breedData = await getBreed(parseInt(breedId));
        setBreed(breedData);

        // Fetch pups of this breed
        const pupsData = await getPups(parseInt(breedId));
        setPups(pupsData);
      } catch (err) {
        setError('Failed to load breed information. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchBreedData();
  }, [breedId]);

  if (loading) {
    return <div className="text-center p-5">Loading breed information...</div>;
  }

  if (error || !breed) {
    return (
      <Alert variant="danger" className="m-3">
        {error || "Couldn't find this breed. Please check the breed ID and try again."}
        <div className="mt-3">
          <Link to="/breeds" className="btn btn-primary">
            Back to Breeds
          </Link>
        </div>
      </Alert>
    );
  }

  return (
    <div>
      <Card className="mb-4 mt-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h1>{breed.name}</h1>
              <div className="d-flex gap-2 mb-3">
                <Badge bg="info">{breed.akc_group}</Badge>
                {breed.is_rare && (
                  <Badge bg="warning" text="dark">
                    Rare Breed
                  </Badge>
                )}
              </div>
            </div>
            <Link to="/breeds" className="btn btn-outline-secondary">
              Back to Breeds
            </Link>
          </div>

          <Row>
            <Col md={8}>
              {breed.description && (
                <div className="breed-description mb-4">
                  <h3>About this Breed</h3>
                  <p>{breed.description}</p>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="breed-examples mt-4">
        <h2>
          {pups.length > 0
            ? `${breed.name}s in our Community`
            : `No ${breed.name}s Yet`}
        </h2>

        {pups.length === 0 ? (
          <Card className="p-4 text-center">
            <p>Be the first to add a {breed.name} to our community!</p>
            <Link to="/upload" className="btn btn-primary">
              Upload a {breed.name}
            </Link>
          </Card>
        ) : (
          <Row className="mt-3">
            {pups.map(pup => (
              <Col key={pup.id} md={4} className="mb-4">
                <PupCard pup={pup} />
              </Col>
            ))}
          </Row>
        )}
      </div>
    </div>
  );
};

export default BreedDetailPage;
